// Telephony Overview.
//
// ── What this page used to be ───────────────────────────────────────────────
// Every number on it was invented, in the source, as a literal: "1,234 calls
// today", "Avg Call Duration 4:32", three AI agents with accuracy percentages,
// campaigns called "Holiday Promotion" at 65% progress, IVR apps on "Twilio"
// and "Exotel". None of it came from anywhere. That is worse than an empty
// screen — an empty screen tells you it has nothing, whereas this told you a
// story, and it read as real to anyone who had not seen the file.
//
// It now shows this organisation's own calls, from GET /calls/summary, which is
// scoped by switch identity on the server. That scoping is the whole reason the
// existing /v1/telephony/data-sources/call-summary could not be reused: it is
// platform-admin only and matches the data source's entire pattern range, so a
// business inside a shared business_id would see its provider's traffic as its
// own — CatchMyCall's 165,461 August calls next to Atmantan's real 39.
//
// Where a number cannot be sourced it is not shown. Agent and campaign counts
// live on their own pages, which already read real data; duplicating them here
// with a second, differently-scoped query is how two screens start disagreeing.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, Phone, PhoneIncoming, PhoneOutgoing, Clock,
  AlertCircle, ArrowRight, Loader2, PhoneMissed, Bot,
} from 'lucide-react';
import { callsService, CallSummary } from 'api/services/calls.service';
import { callHangupMetricsService, HangupSummaryRow } from 'api/services/callHangupMetrics.service';

/** Seconds as m:ss — the shape the old hardcoded "4:32" implied. */
const asDuration = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return '0:00';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const asCount = (n?: number): string => (n === undefined || n === null ? '—' : n.toLocaleString());

const TelephonyDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = useState<CallSummary | null>(null);
  const [causes, setCauses] = useState<HangupSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [s, h] = await Promise.all([
      callsService.getSummary(),
      callHangupMetricsService.summary(),
    ]);
    if (s.success) setSummary(s.data as CallSummary);
    else setError((s as any).error || 'Failed to load call summary');
    // Hangup causes are supporting detail: a failure there must not blank the
    // whole page, so it is left empty rather than raised.
    if (h.success) setCauses((h.data as HangupSummaryRow[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = [
    { label: 'Total Calls', value: asCount(summary?.totalCalls), icon: Phone },
    { label: 'Inbound', value: asCount(summary?.inbound), icon: PhoneIncoming },
    { label: 'Outbound', value: asCount(summary?.outbound), icon: PhoneOutgoing },
    { label: 'Avg Duration', value: asDuration(summary?.avgDurationSeconds), icon: Clock },
  ];

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">Telephony Overview</h1>
          <p className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mt-1">
            Your calls over the last 7 days
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="text-sm underline underline-offset-2 disabled:opacity-50">
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={load} disabled={loading}
                  className="underline underline-offset-2 font-medium disabled:opacity-50 whitespace-nowrap">
            {loading ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      )}

      {/* "Nowhere to look" is a different answer from "nothing happened", and
          the difference is what tells someone whether to go and configure
          something. The server distinguishes them; so does this. */}
      {summary?.note && (
        <div className="text-[12.5px] px-3 py-2 rounded-lg"
             style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
          {summary.note}
        </div>
      )}

      {summary?.partial && (
        <div className="text-[12.5px] px-3 py-2 rounded-lg"
             style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
          One or more call sources did not answer — these totals are incomplete.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label}
                 className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
              <div className="flex items-center justify-between mb-3 lg:mb-4">
                <div className="p-2 lg:p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-gray-600 dark:text-gray-300" />
                </div>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
              </div>
              <p className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mt-1">{stat.label}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Answered vs missed — derived from the same totals, never a second query. */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Outcome</h2>
          {loading && !summary ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <PhoneIncoming className="w-4 h-4" /> Answered
                </span>
                <span className="font-medium text-gray-900 dark:text-white">{asCount(summary?.answered)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <PhoneMissed className="w-4 h-4" /> Missed
                </span>
                <span className="font-medium text-gray-900 dark:text-white">{asCount(summary?.missed)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Total talk time</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {asDuration(summary?.durationSeconds)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Why calls ended. Empty until the hangup-metrics source has rows for
            this organisation — shown as empty rather than filled with an
            example, which is the mistake this page is being rescued from. */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Why calls ended</h2>
          {causes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {loading ? 'Loading…' : 'No hangup data for this period.'}
            </p>
          ) : (
            <div className="space-y-2 text-sm">
              {causes.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <span className="text-gray-600 dark:text-gray-400 truncate">
                    {c.hangup_cause_txt || c.disposition || `Cause ${c.hangup_cause ?? '—'}`}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {(c.count ?? 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Links, not counts. The agent and campaign pages own those numbers and
          already read them correctly; restating them here from a differently
          scoped query is how two screens begin to disagree. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button onClick={() => navigate('/agents')}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between hover:border-indigo-300">
          <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
            <Bot className="w-4 h-4" /> AI Agents
          </span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </button>
        <button onClick={() => navigate('/telephony/campaigns')}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between hover:border-indigo-300">
          <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
            <Megaphone className="w-4 h-4" /> Campaigns
          </span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default TelephonyDashboard;
